import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { FEATURE_CATEGORIES, GET_STARTED_URL } from "@/data/site";

export default function Features() {
  return (
    <div data-testid="features-page">
      <PageHero
        overline="Features"
        titleLines={["Everything an", "institution needs."]}
        subtitle="From people and attendance to flexible fees and communication — Activity Hub Manager runs the whole institution, your way. Nothing hardcoded, nothing rigid."
      />

      {/* Category nav */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap justify-center gap-2 rounded-2xl border border-zinc-800 bg-surface p-3" data-testid="feature-category-nav">
            {FEATURE_CATEGORIES.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                data-testid={`feature-nav-${c.id}`}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-zinc-400 transition-colors hover:bg-white/5 hover:text-white"
              >
                <c.icon className="h-4 w-4 text-brand" />
                {c.title}
              </a>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Categories */}
      {FEATURE_CATEGORIES.map((cat, ci) => (
        <section
          key={cat.id}
          id={cat.id}
          data-testid={`feature-category-${cat.id}`}
          className="mx-auto max-w-7xl scroll-mt-24 px-5 py-16 lg:px-8"
        >
          <Reveal className="mb-10 flex flex-col gap-4 border-b border-white/10 pb-8 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-md border border-zinc-700 bg-black/40 text-brand">
                  <cat.icon className="h-5 w-5" />
                </span>
                <span className="text-xs font-semibold uppercase tracking-[0.25em] text-brand">
                  {String(ci + 1).padStart(2, "0")} / {cat.title}
                </span>
              </div>
              <h2 className="font-heading text-3xl font-bold tracking-tighter text-white sm:text-4xl">{cat.title}</h2>
              <p className="mt-3 text-zinc-400">{cat.blurb}</p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {cat.items.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 0.06}>
                <div className="group flex h-full flex-col rounded-xl border border-zinc-800 bg-surface p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand/70 hover:shadow-[0_0_40px_rgba(0,85,255,0.12)]">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md border border-zinc-700 bg-black/40 text-brand transition-colors group-hover:border-brand">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-white">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-center justify-between gap-6 rounded-2xl border border-zinc-800 bg-surface p-10 text-center sm:flex-row sm:text-left">
            <div>
              <h3 className="font-heading text-2xl font-bold tracking-tight text-white">See it in action.</h3>
              <p className="mt-2 text-zinc-400">Start your free trial and set up your institution in minutes.</p>
            </div>
            <a href={GET_STARTED_URL} data-testid="features-cta-btn" className="inline-flex shrink-0 items-center gap-2 rounded-md bg-brand px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover">
              Get Started <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
